import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import CustomPropTypes from 'CustomPropTypes';
import { Table } from 'antd';
import { Link } from 'react-router-dom';
import MaintenanceRecordAddForm from './maintenance-record-add-form';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMaintenanceRecordsByMachineId, fetchMaintenanceRecordsDetails } from './store/slice';
import { selectMachineMaintenanceRecordsData, selectMaintenanceRecordData } from './store/selectors';
import { format } from 'date-fns';

const initData = {
  id: 0,
  workOrderNumber: '',
  date: '',
  description: '',
  maintenanceType: '1',
  personnelName: '',
  downTime: '',
  pictures: '',
  serviceRecords: [],
  personnelRecords: [
    {
      personnelName: '',
      startTime: new Date().toISOString().slice(0, 16),
      endTime: new Date().toISOString().slice(0, 16),
    },
  ],
};

const formatDate = date => {
  if (!date) return '';
  try {
    return format(new Date(date), 'MM/dd/yyyy');
  } catch (err) {
    console.error('Invalid date:', date);
    return '';
  }
};

const MaintenanceHistory = ({ data, onCloseModal, machineId, focusedMaintenanceId }) => {
  const [formData, setFormData] = useState({});
  const [showMaintenanceHistory, setshowMaintenanceHistory] = useState(true);
  const [viewingRecord, setViewingRecord] = useState(null);
  const openedFocusRef = useRef(false);

  const machineMaintenanceRecordsData = useSelector(selectMachineMaintenanceRecordsData);
  const maintenanceRecordData = useSelector(selectMaintenanceRecordData);

  const maintenanceTypeOptions = [
    { value: 1, label: 'Preventative/Predictive' },
    { value: 2, label: 'Machine Down' },
    { value: 3, label: 'Safety Check' },
  ];

    const dispatch = useDispatch();
    useEffect(() => {
      dispatch(fetchMaintenanceRecordsByMachineId(machineId));
    }, []);

  useEffect(() => {
    if (viewingRecord && maintenanceRecordData?.workOrderNumber) {
      setFormData(maintenanceRecordData);
      setshowMaintenanceHistory(false);
    }
  }, [viewingRecord, maintenanceRecordData]);

  const handleViewClick = record => {
    setViewingRecord(record);
    dispatch(
      fetchMaintenanceRecordsDetails({
        maintenanceId: record.maintenanceId,
        machineId: record.machineId,
        workOrderNumber: record.workOrderNumber,
      }),
    );
  };

  useEffect(() => {
    if (openedFocusRef.current || !focusedMaintenanceId) {
      return;
    }
    const records = machineMaintenanceRecordsData || [];
    const record = records.find(
      item => Number(item.maintenanceId) === Number(focusedMaintenanceId),
    );
    if (!record) {
      return;
    }
    openedFocusRef.current = true;
    handleViewClick(record);
  }, [focusedMaintenanceId, machineMaintenanceRecordsData]);

  const handleMaintenanceRecord = () => {
    setFormData(initData); // reset form to empty
    setViewingRecord(null); // no record being viewed
    setshowMaintenanceHistory(!showMaintenanceHistory);
  };

  const columns = [
    { title: 'Work Order Number', dataIndex: 'workOrderNumber', key: 'workOrderNumber' },
    { title: 'Date', dataIndex: 'date', key: 'date', render: date => formatDate(date)},
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: 'Preventative/Predictive',
      dataIndex: 'maintenanceType',
      key: 'maintenanceType',
      render: maintenanceTypeValue => (
        <span>
          {maintenanceTypeOptions.find(item => item.value == maintenanceTypeValue)?.label}
        </span>
      ),
    },
    { title: 'Personnel Name', dataIndex: 'personnelName', key: 'personnelName' },
    { title: 'Down Time Duration', dataIndex: 'downTime', key: 'downTime' },
    {
      title: 'Action',
      dataIndex: 'id',
      key: 'id',
      render: (text, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <Link className="me-2 p-2" onClick={() => handleViewClick(record)}>
              View
            </Link>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="mb-3">
        <button className="btn btn-info me-2 " onClick={() => handleMaintenanceRecord()}>
          {showMaintenanceHistory ? '+ Add Maintenance Record' : '← Back to Maintenance History'}
        </button>
      </div>
      {showMaintenanceHistory ? (
        <div className="row">
          <div className="col-lg-12 col-md-12 col-sm-12">
            <Table columns={columns} dataSource={machineMaintenanceRecordsData } />
          </div>
        </div>
      ) : (
        <MaintenanceRecordAddForm
          key={formData?.workOrderNumber || 'new'}
          data={formData}
          machineId={machineId}
          maintenanceTypeOptions={maintenanceTypeOptions}
          onCloseModal={onCloseModal}
        />
      )}
    </>
  );
};

MaintenanceHistory.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  data: PropTypes.shape(CustomPropTypes.machines),
  onChanges: PropTypes.func,
  machineId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  focusedMaintenanceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default MaintenanceHistory;
